import * as React from 'react';
import { Dimensions, Pressable, View } from 'react-native';
import { MultiStepModal } from '@/components/ui/multi-step-modal';
import { Spotlight } from '@/components/ui/spotlight';
import type { TargetLayout } from '@/components/ui/spotlight';
import { Text } from '@/components/ui/text';
import { useTourTargets } from '../contexts/tour-target-registry';
import { TourTooltipCard } from './tour-tooltip-card';
import type { EligibleTour, TourStepAction } from '../types';

interface TourRendererProps {
  tour: EligibleTour;
  currentStep: number;
  onAdvanceStep: () => void;
  onComplete: () => void;
  onDismiss: () => void;
}

const MEASURE_RETRY_DELAY = 100;

function TourRenderer({
  tour,
  currentStep,
  onAdvanceStep,
  onComplete,
  onDismiss,
}: TourRendererProps) {
  const { steps } = tour.definition;
  const totalSteps = steps.length;
  const { measureTarget } = useTourTargets();
  const [targetLayout, setTargetLayout] = React.useState<TargetLayout | null>(null);

  if (totalSteps === 0) {
    return null;
  }

  const step = steps[currentStep];
  if (!step) {
    return null;
  }

  const isSpotlightStep = step.spotlight && step.targetSelector != null;

  React.useEffect(() => {
    if (!isSpotlightStep || !step.targetSelector) {
      setTargetLayout(null);
      return;
    }

    let cancelled = false;
    const selector = step.targetSelector;

    async function measure() {
      const layout = await measureTarget(selector);
      if (cancelled) return;

      if (layout) {
        setTargetLayout(layout);
      } else {
        await new Promise((r) => setTimeout(r, MEASURE_RETRY_DELAY));
        if (cancelled) return;
        const retryLayout = await measureTarget(selector);
        if (!cancelled) {
          setTargetLayout(retryLayout);
        }
      }
    }

    measure();

    return () => {
      cancelled = true;
    };
  }, [currentStep, step.targetSelector, isSpotlightStep, measureTarget]);

  React.useEffect(() => {
    if (!isSpotlightStep || !step.targetSelector) return;

    const selector = step.targetSelector;

    function handleDimensionChange() {
      measureTarget(selector).then((layout) => {
        if (layout) setTargetLayout(layout);
      });
    }

    const subscription = Dimensions.addEventListener('change', handleDimensionChange);
    return () => subscription.remove();
  }, [isSpotlightStep, step.targetSelector, measureTarget]);

  function handleAction(action: TourStepAction) {
    switch (action.behavior) {
      case 'next':
        onAdvanceStep();
        break;
      case 'complete':
        onComplete();
        break;
      case 'dismiss':
      case 'skip':
        onDismiss();
        break;
      default:
        break;
    }
  }

  if (isSpotlightStep && targetLayout) {
    return (
      <Spotlight
        visible={true}
        targetLayout={targetLayout}
        onDismiss={onDismiss}
        testID="tour-spotlight"
      >
        <TourTooltipCard
          step={step}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onAction={handleAction}
        />
      </Spotlight>
    );
  }

  const displayStep = currentStep + 1;

  return (
    <MultiStepModal
      visible={true}
      currentStep={displayStep}
      totalSteps={totalSteps}
      testID="tour-renderer-modal"
    >
      <View className="flex-1">
        <Text className="text-card-foreground text-lg font-semibold mb-3">
          {step.title}
        </Text>
        <Text className="text-muted-foreground text-base mb-6">
          {step.body}
        </Text>

        <View className="flex-row justify-end gap-3">
          {step.secondaryAction && (
            <Pressable
              onPress={() => handleAction(step.secondaryAction!)}
              accessibilityLabel={step.secondaryAction.label}
              accessibilityRole="button"
              className="rounded-lg py-3 px-6 items-center"
            >
              <Text className="text-muted-foreground font-semibold text-base">
                {step.secondaryAction.label}
              </Text>
            </Pressable>
          )}
          <Pressable
            onPress={() => handleAction(step.primaryAction)}
            accessibilityLabel={step.primaryAction.label}
            accessibilityRole="button"
            className="bg-primary rounded-lg py-3 px-6 items-center"
          >
            <Text className="text-primary-foreground font-semibold text-base">
              {step.primaryAction.label}
            </Text>
          </Pressable>
        </View>
      </View>
    </MultiStepModal>
  );
}

export { TourRenderer };
export type { TourRendererProps };
